import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Popup from 'src/component/Popup';
import { Store } from 'src/store';
import getDesignId from 'src/store/design/selector/getDesignId';
import getOrderItemId from 'src/store/design/selector/getOrderItemId';
import getAbstractProduct from 'src/store/design/selector/getAbstractProduct';
import getProductId from 'src/store/design/selector/getProductId';
import getForwardUrl from 'src/store/app/selector/getForwardUrl';
import getIsMobile from 'src/store/app/selector/getIsMobile';
import isPopupActive from 'src/store/app/selector/isPopupActive';
import goToOptimalprintOperation from 'src/store/app/operation/goToOptimalprintOperation';
import showBackdropOperation from 'src/store/app/operation/showBackdropOperation';
import { EDITOR_CROSSSELL_POPUP_ID } from 'src/store/app/types';
import { getAttributeValue } from 'src/util/formats/generateFormatTabData';
import styles from './index.module.scss';

// Preset selecting the curated cross-sell product list from the recommendation
// config (cross-sell/v3), scoped to the editor placement. The matching
// recommendation_block_products entry (locale + preset "editor") must exist,
// otherwise the list resolves empty and the popup is skipped.
const EDITOR_CROSSSELL_PRESET = 'editor';

// Post-add cross-sell modal shown over the editor. Reuses the shared
// op-live-recommendations carousel (basket + oneclick-add): it renders the
// customer's just-saved design applied to the curated products and adds each
// directly to the same cart, without re-entering the editor.
//
// The trigger opens the popup without a backdrop, so this mounts hidden and the
// carousel prefetches. Only once it reports items (>0) do we reveal the modal +
// backdrop; an empty placement continues straight to the cart with nothing shown.
const EditorCrossSellPopup = () => {
  const dispatch = useDispatch<ThunkDispatch<Store, undefined, AnyAction>>();
  const { t } = useTranslation();
  const designId = useSelector(getDesignId);
  const orderItemId = useSelector(getOrderItemId);
  const forwardUrl = useSelector(getForwardUrl);
  const isActive = useSelector((store: Store) => isPopupActive(store, EDITOR_CROSSSELL_POPUP_ID));
  const abstractProduct = useSelector(getAbstractProduct);
  const productId = useSelector(getProductId);
  const isMobile = useSelector(getIsMobile);
  const [ready, setReady] = useState(false);

  const goToCart = () => dispatch(goToOptimalprintOperation(forwardUrl));

  // Name the variant that landed in the basket ("S · White") so the confirmation is
  // verifiable. Only the selector options are used: they are already translated, whereas
  // abstractProduct.name is an op-web key (abstractProduct_<id>) that this app cannot
  // resolve, and rendering it leaks the raw key into the UI.
  const addedProduct = abstractProduct?.products.find((product) => product.productId === productId);
  const addedProductLabel = addedProduct && (abstractProduct?.editorSelectors || [])
    .filter((selector) => !selector.isHidden)
    .map((selector) => selector.options.find(
      (option) => option.value === getAttributeValue(addedProduct as any, selector.typeName),
    )?.name)
    .filter(Boolean)
    .join(' · ');

  // Reset the reveal state whenever the popup closes so the next add prefetches
  // again instead of flashing the previous result.
  useEffect(() => {
    if (!isActive) setReady(false);
  }, [isActive]);

  useEffect(() => {
    // The carousel signals load completion with the resolved count. Reveal only
    // when it has something to show; otherwise continue to the cart as a normal
    // add would, without ever showing an empty shell.
    const onLoaded = (e: Event) => {
      const { detail } = e as CustomEvent<{ count?: number; blockName?: string }>;
      if (detail?.blockName !== 'Editor') return;
      if (detail?.count && detail.count > 0) {
        setReady(true);
        dispatch(showBackdropOperation(true));
      } else {
        dispatch(goToOptimalprintOperation(forwardUrl));
      }
    };
    window.addEventListener('cross-sell-loaded', onLoaded);
    return () => window.removeEventListener('cross-sell-loaded', onLoaded);
  }, [dispatch, forwardUrl]);

  return (
    <Popup
      popupId={EDITOR_CROSSSELL_POPUP_ID}
      rounded
      wide
      visible={ready}
      // Header states the offer; add confirmations live where the add happened (named
      // below for the editor item, on the card itself for one-click adds).
      title={t('txt_editor_crosssell_title')}
      applyButton={(
        // Full width on mobile for thumb reach; on the wide desktop popup a full-bleed
        // dark bar outweighs the cards, so let it size to its label instead.
        <op-button block={isMobile || undefined} variant="primary" size="big" onClick={goToCart}>
          {t('txt_editor_crosssell_go_to_cart')}
        </op-button>
      )}
    >
      {addedProductLabel && (
        <div className={styles.addedItem}>
          <span className={styles.addedItemIcon}>&#10003;</span>
          {addedProductLabel}
          <span className={styles.addedItemState}>
            {t('txt_editor_crosssell_added_to_cart')}
          </span>
        </div>
      )}
      <op-live-recommendations
        variant="editor"
        oneclick-add=""
        show-header="false"
        block-name="Editor"
        preset={EDITOR_CROSSSELL_PRESET}
        customer-design-id={designId ? String(designId) : ''}
        order-item-id={orderItemId ? String(orderItemId) : ''}
      />
    </Popup>
  );
};

export default EditorCrossSellPopup;
